import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Trophy,
  Save,
  Lock,
  Sparkles,
  ListChecks,
  List,
  GitBranch,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Flag } from "@/components/Flag";
import { RankBadge } from "@/components/RankBadge";
import { CountryCombobox } from "@/components/CountryCombobox";
import { COUNTRY_BY_CCA3 } from "@/lib/fifa-countries";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  getAdminTournament,
  seedR32Match,
  previewR32SeedChange,
  updateMatchResult,
} from "@/lib/tournament.functions";
import { verifyIsAdmin } from "@/lib/admin.functions";
import { listTournamentGroups, type GroupRow } from "@/lib/tournament-groups.functions";
import {
  listGroupMatches,
  type GroupMatchRow,
} from "@/lib/tournament-group-matches.functions";
import { formatLabel } from "@/lib/tournament-formats";
import { FinalizePanel } from "@/components/admin/FinalizePanel";
import { GroupSeedingPanel } from "@/components/admin/GroupSeedingPanel";
import { GroupResultsPanel } from "@/components/admin/GroupResultsPanel";
import { MatchRecapButton } from "@/components/MatchRecapButton";
import { GoblinRecapDialog } from "@/components/GoblinRecapDialog";

export const Route = createFileRoute("/_app/admin/tournaments/$tournamentType")({
  component: TournamentAdminPage,
  head: () => ({ meta: [{ title: "Manage tournament — Admin" }] }),
});

// Format display name comes from the `tournament_formats` registry via the
// `formatLabel` helper (with a baked-in fallback). No hardcoded list here.

type Detail = Awaited<ReturnType<typeof getAdminTournament>>;
type Team = Detail["teams"][number];
type Match = Detail["matches"][number];

function TournamentAdminPage() {
  const { tournamentType } = Route.useParams();
  const { session, loading } = useAuth();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const isGroupFormat = tournamentType === "fifa_wc_mens_group_knockout";
  const [tab, setTab] = useState<
    "group-seeding" | "group-results" | "seeding" | "results"
  >(isGroupFormat ? "group-seeding" : "seeding");
  const [serverIsAdmin, setServerIsAdmin] = useState<boolean | null>(null);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [groupMatches, setGroupMatches] = useState<GroupMatchRow[]>([]);

  const verifyAdminFn = useAuthedServerFn(verifyIsAdmin);
  const getFn = useAuthedServerFn(getAdminTournament);
  const seedFn = useAuthedServerFn(seedR32Match);
  const previewSeedFn = useAuthedServerFn(previewR32SeedChange);
  const updateResultFn = useAuthedServerFn(updateMatchResult);
  const listGroupsFn = useAuthedServerFn(listTournamentGroups);
  const listGroupMatchesFn = useAuthedServerFn(listGroupMatches);

  // Confirmation state for R32 seed changes that would delete user picks.
  const [seedConfirm, setSeedConfirm] = useState<{
    matchCode: string;
    picksToDelete: number;
    entriesAffected: number;
    leaguesAffected: number;
    resolve: (ok: boolean) => void;
  } | null>(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const [res, gr, gm] = await Promise.all([
        getFn({
          data: { tournamentType: tournamentType as "fifa_wc_mens_knockout" | "fifa_wc_mens_group_knockout" },
        }),
        isGroupFormat
          ? listGroupsFn({ data: { tournamentType: "fifa_wc_mens_group_knockout" } })
          : Promise.resolve({ groups: [] as GroupRow[] }),
        isGroupFormat
          ? listGroupMatchesFn({ data: { tournamentType: "fifa_wc_mens_group_knockout" } })
          : Promise.resolve({ matches: [] as GroupMatchRow[] }),
      ]);
      setDetail(res);
      setGroups(gr.groups);
      setGroupMatches(gm.matches);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to load tournament";
      toast.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!session) {
      setServerIsAdmin(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await verifyAdminFn();
        if (cancelled) return;
        setServerIsAdmin(res.isAdmin);
        if (res.isAdmin) await load();
      } catch {
        if (!cancelled) setServerIsAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, tournamentType]);

  if (loading || (session && serverIsAdmin === null)) {
    return (
      <main className="max-w-[1200px] mx-auto px-4 py-10">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </main>
    );
  }
  if (!serverIsAdmin) {
    return (
      <main className="max-w-[1200px] mx-auto px-4 py-10">
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6">
          <div className="text-sm font-semibold text-destructive">Forbidden</div>
          <p className="mt-1 text-sm text-muted-foreground">
            You must be an admin to view this page.
          </p>
        </div>
      </main>
    );
  }

  if (!detail) {
    return (
      <main className="max-w-[1200px] mx-auto px-4 py-10">
        <div className="text-sm text-muted-foreground">Loading tournament…</div>
      </main>
    );
  }

  const teamById = new Map(detail.teams.map((t) => [t.id, t]));
  const matchByCode = new Map(detail.matches.map((m) => [m.match_code, m]));
  const r32Matches = detail.matches.filter((m) => m.round === "r32");
  const r32Seeded = r32Matches.filter((m) => m.home_team_id && m.away_team_id).length;
  const totalMatches = detail.matches.length;
  const decided = detail.matches.filter((m) => m.winner_team_id).length;

  // cca3 codes already used in any R32 slot — for combobox de-dupe.
  const usedCodes = new Set<string>();
  for (const m of r32Matches) {
    if (m.home_team_id) {
      const t = teamById.get(m.home_team_id);
      if (t) usedCodes.add(t.code.toUpperCase());
    }
    if (m.away_team_id) {
      const t = teamById.get(m.away_team_id);
      if (t) usedCodes.add(t.code.toUpperCase());
    }
  }

  // R32 search allowlist: only the teams placed into groups for this
  // tournament. For group-knockout, also drop any team the admin has
  // marked as 4th in their group (4th place doesn't advance). 1st, 2nd,
  // and 3rd remain — the admin sorts out the 3rd-place math themselves.
  // When no groups are set up (e.g. legacy knockout-only format), pass
  // `undefined` so the full FIFA nation list is shown.
  const r32AllowedCodes: string[] | undefined = isGroupFormat
    ? (() => {
        const codes = new Set<string>();
        for (const g of groups) {
          for (const t of g.teams) {
            if (t.actualPosition === 4) continue;
            codes.add(t.code.toUpperCase());
          }
        }
        return Array.from(codes);
      })()
    : undefined;

  return (
    <>
    <main className="max-w-[1200px] mx-auto px-4 py-6 sm:py-10">
      <div className="mb-6">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3"
        >
          <ArrowLeft className="w-3 h-3" /> Back to admin
        </Link>
        <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-2 flex-wrap">
          <Trophy className="w-6 h-6 text-primary" />
          {formatLabel(tournamentType)}
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
            Shared across all leagues
          </span>
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Edits here apply to every league using this tournament. R32 seeding +
          results entered here automatically score every league&apos;s picks.
        </p>
      </div>

      {(() => {
        const groupsSeeded = groups.filter((g) => g.teams.length === 4).length;
        const groupsTotal = groups.length;
        const groupResultsEntered = groupMatches.filter((m) => m.resultEnteredAt).length;
        const groupMatchesTotal = groupMatches.length;
        return (
          <div
            className={cn(
              "grid gap-3 mb-6",
              isGroupFormat
                ? "grid-cols-2 sm:grid-cols-4"
                : "grid-cols-2",
            )}
          >
            {isGroupFormat && (
              <>
                <Stat
                  label="Groups seeded"
                  value={`${groupsSeeded} / ${groupsTotal || 12}`}
                />
                <Stat
                  label="Group results"
                  value={`${groupResultsEntered} / ${groupMatchesTotal || 72}`}
                />
              </>
            )}
            <Stat label="R32 matches seeded" value={`${r32Seeded} / 16`} />
            <Stat label="Knockout results" value={`${decided} / ${totalMatches}`} />
          </div>
        );
      })()}

      <div className="inline-flex items-center rounded-lg border border-border bg-card p-0.5 mb-4 flex-wrap">
        {isGroupFormat && (
          <>
            <TabBtn
              active={tab === "group-seeding"}
              onClick={() => setTab("group-seeding")}
              icon={<List className="w-3.5 h-3.5" />}
              label="Group Seeding"
            />
            <TabBtn
              active={tab === "group-results"}
              onClick={() => setTab("group-results")}
              icon={<Sparkles className="w-3.5 h-3.5" />}
              label="Group Results"
            />
          </>
        )}
        <TabBtn
          active={tab === "seeding"}
          onClick={() => setTab("seeding")}
          icon={<ListChecks className="w-3.5 h-3.5" />}
          label="Knockout Seeding"
        />
        <TabBtn
          active={tab === "results"}
          onClick={() => setTab("results")}
          icon={<Sparkles className="w-3.5 h-3.5" />}
          label="Knockout Results"
        />
      </div>

      {tab === "group-seeding" && isGroupFormat && (
        <GroupSeedingPanel tournamentType="fifa_wc_mens_group_knockout" />
      )}

      {tab === "group-results" && isGroupFormat && (
        <GroupResultsPanel tournamentType="fifa_wc_mens_group_knockout" />
      )}

      {refreshing && (
        <div className="text-xs text-muted-foreground mb-2">Refreshing…</div>
      )}

      {tab === "seeding" && (
        <SeedingPanel
          matches={r32Matches}
          teamById={teamById}
          usedCodes={usedCodes}
          allowedCodes={r32AllowedCodes}
          tournamentType={tournamentType as "fifa_wc_mens_knockout" | "fifa_wc_mens_group_knockout"}
          onSave={async (matchId, homeCca3, awayCca3) => {
            const tType = tournamentType as
              | "fifa_wc_mens_knockout"
              | "fifa_wc_mens_group_knockout";
            const homePayload = homeCca3
              ? (() => {
                  const c = COUNTRY_BY_CCA3.get(homeCca3);
                  if (!c) throw new Error("Unknown country");
                  return c;
                })()
              : null;
            const awayPayload = awayCca3
              ? (() => {
                  const c = COUNTRY_BY_CCA3.get(awayCca3);
                  if (!c) throw new Error("Unknown country");
                  return c;
                })()
              : null;

            // Preview impact: if this change would delete user picks, force
            // an explicit confirm. Block early if a result is already entered.
            const preview = await previewSeedFn({
              data: {
                matchId,
                tournamentType: tType,
                homeCca3: homeCca3 ?? null,
                awayCca3: awayCca3 ?? null,
              },
            });
            if (preview.hasResult && preview.teamsChanging) {
              toast.error(
                "This match has a result entered. Clear the result first, then change seeding.",
              );
              return;
            }
            if (preview.teamsChanging && preview.picksToDelete > 0) {
              const ok = await new Promise<boolean>((resolve) => {
                setSeedConfirm({
                  matchCode: preview.matchCode,
                  picksToDelete: preview.picksToDelete,
                  entriesAffected: preview.entriesAffected,
                  leaguesAffected: preview.leaguesAffected,
                  resolve,
                });
              });
              if (!ok) return;
            }

            const res = await seedFn({
              data: {
                matchId,
                tournamentType: tType,
                home: homePayload,
                away: awayPayload,
              },
            });
            if (res.deletedPickCount > 0) {
              toast.success(
                `Match updated. Deleted ${res.deletedPickCount} downstream pick${res.deletedPickCount === 1 ? "" : "s"} across ${res.affectedEntryCount} entr${res.affectedEntryCount === 1 ? "y" : "ies"}.`,
              );
            } else {
              toast.success("Match updated");
            }
            await load();
          }}
        />
      )}

      {tab === "results" && (
        <div className="space-y-6">
          <FinalizePanel
            tournamentType={tournamentType as "fifa_wc_mens_knockout" | "fifa_wc_mens_group_knockout"}
            teams={detail.teams}
          />
          <ResultsPanel
            matches={detail.matches}
            matchByCode={matchByCode}
            teams={detail.teams}
            onSave={async (matchId, payload) => {
              await updateResultFn({
                data: {
                  matchId,
                  winnerTeamId: payload.winnerTeamId,
                  homeScore: payload.homeScore,
                  awayScore: payload.awayScore,
                  yellowCards: payload.yellowCards,
                  redCards: payload.redCards,
                  wentToExtraTime: payload.wentToExtraTime,
                  wentToShootout: payload.wentToShootout,
                  pksAwarded: payload.pksAwarded,
                },
              });
              toast.success(payload.winnerTeamId ? "Result saved" : "Result cleared");
              // Refresh in the background so the editor sheet can close and
              // re-enable its Save button immediately. Awaiting load() here
              // kept the button disabled until every dependent fetch finished,
              // which made saves feel hung until the user clicked away.
              void load();
            }}
          />
        </div>
      )}
    </main>
    <AlertDialog
      open={!!seedConfirm}
      onOpenChange={(open) => {
        if (!open && seedConfirm) {
          seedConfirm.resolve(false);
          setSeedConfirm(null);
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Change {seedConfirm?.matchCode ?? "R32 match"} seeding?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete{" "}
            <strong>
              {seedConfirm?.picksToDelete ?? 0} pick
              {seedConfirm?.picksToDelete === 1 ? "" : "s"}
            </strong>{" "}
            across{" "}
            <strong>
              {seedConfirm?.entriesAffected ?? 0} entr
              {seedConfirm?.entriesAffected === 1 ? "y" : "ies"}
            </strong>{" "}
            in{" "}
            <strong>
              {seedConfirm?.leaguesAffected ?? 0} league
              {seedConfirm?.leaguesAffected === 1 ? "" : "s"}
            </strong>
            . Affected members will need to re-pick this match and everything
            downstream of it. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              seedConfirm?.resolve(false);
              setSeedConfirm(null);
            }}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              seedConfirm?.resolve(true);
              setSeedConfirm(null);
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Yes, change seeding & delete picks
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

/* ============================================================
   Sub-components
============================================================ */

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
        {label}
      </div>
      <div className="font-display text-2xl font-bold text-foreground tabular-nums mt-0.5">
        {value}
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function TeamLabel({ team }: { team: Team | null | undefined }) {
  if (!team) return <span className="text-muted-foreground italic">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Flag code={team.code} size={14} />
      <span>{team.name}</span>
      <RankBadge code={team.code} size="xs" />
    </span>
  );
}

/* ============================================================
   R32 Seeding panel
============================================================ */

type SeedSaver = (
  matchId: string,
  homeCca3: string | null,
  awayCca3: string | null,
) => Promise<void>;

function SeedingPanel({
  matches,
  teamById,
  usedCodes,
  allowedCodes,
  tournamentType: _tournamentType,
  onSave,
}: {
  matches: Match[];
  teamById: Map<string, Team>;
  usedCodes: Set<string>;
  allowedCodes?: string[];
  tournamentType: "fifa_wc_mens_knockout" | "fifa_wc_mens_group_knockout";
  onSave: SeedSaver;
}) {
  const [view, setView] = useState<"list" | "tree">("tree");

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-muted/20 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground">
            Round of 32 matchups
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Pick the two teams for each R32 match as group play resolves them.
            {allowedCodes
              ? " Search is limited to teams in the tournament groups; teams marked 4th in their group are hidden."
              : " Type a country name to search the full FIFA-nation list."}
          </div>
        </div>
        <div className="inline-flex items-center rounded-md border border-border bg-background p-0.5 shrink-0">
          <button
            type="button"
            onClick={() => setView("list")}
            className={cn(
              "inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors",
              view === "list"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
            aria-pressed={view === "list"}
          >
            <List className="w-3 h-3" />
            List
          </button>
          <button
            type="button"
            onClick={() => setView("tree")}
            className={cn(
              "inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors",
              view === "tree"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
            aria-pressed={view === "tree"}
          >
            <GitBranch className="w-3 h-3" />
            Tree
          </button>
        </div>
      </div>
      {view === "list" ? (
        <ul className="divide-y divide-border">
          {matches.map((m) => (
            <SeedingRow
              key={m.id}
              match={m}
              teamById={teamById}
              usedCodes={usedCodes}
              allowedCodes={allowedCodes}
              onSave={onSave}
            />
          ))}
        </ul>
      ) : (
        <SeedingTree
          matches={matches}
          teamById={teamById}
          usedCodes={usedCodes}
          allowedCodes={allowedCodes}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function SeedingTree({
  matches,
  teamById,
  usedCodes,
  allowedCodes,
  onSave,
}: {
  matches: Match[];
  teamById: Map<string, Team>;
  usedCodes: Set<string>;
  allowedCodes?: string[];
  onSave: SeedSaver;
}) {
  const SLOT = 156;
  const GAP = 12;
  const COLUMN_HEADER_H = 32;

  const COLUMNS: { label: string; count: number }[] = [
    { label: "R32", count: 16 },
    { label: "R16", count: 8 },
    { label: "QF", count: 4 },
    { label: "SF", count: 2 },
    { label: "Final", count: 1 },
  ];

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const colRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [leftmostVisible, setLeftmostVisible] = useState(0);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;

    const recompute = () => {
      const rootLeft = root.getBoundingClientRect().left;
      let firstVisible = 0;
      for (let i = 0; i < colRefs.current.length; i++) {
        const el = colRefs.current[i];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.right > rootLeft + 16) {
          firstVisible = i;
          break;
        }
      }
      setLeftmostVisible((prev) => (prev === firstVisible ? prev : firstVisible));
    };

    recompute();
    root.addEventListener("scroll", recompute, { passive: true });
    window.addEventListener("resize", recompute);
    return () => {
      root.removeEventListener("scroll", recompute);
      window.removeEventListener("resize", recompute);
    };
  }, []);

  const visibleContentHeight = (() => {
    let max = 0;
    for (let i = leftmostVisible; i < COLUMNS.length; i++) {
      const depth = i - leftmostVisible;
      const slotH = SLOT * Math.pow(2, depth) - GAP;
      const count = COLUMNS[i].count;
      const colH = COLUMN_HEADER_H + count * slotH + Math.max(0, count - 1) * GAP;
      if (colH > max) max = colH;
    }
    return max;
  })();

  return (
    <div className="p-3">
      <p className="mb-2 px-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        Bracket preview · scroll horizontally · later rounds compact as you scroll
      </p>
      <div
        ref={scrollRef}
        className="overflow-x-auto overflow-y-hidden pb-2 transition-[height] duration-300 ease-out"
        style={{ height: visibleContentHeight + 16 }}
      >
        <div className="flex items-stretch gap-6" style={{ minWidth: "max-content" }}>
          {COLUMNS.map((col, colIdx) => {
            const effectiveDepth = Math.max(0, colIdx - leftmostVisible);
            return (
              <div
                key={col.label}
                ref={(el) => {
                  colRefs.current[colIdx] = el;
                }}
                className="flex flex-col"
              >
                <TreeColumnInner
                  label={col.label}
                  count={col.count}
                  slot={SLOT}
                  gap={GAP}
                  depth={effectiveDepth}
                >
                  {col.label === "R32"
                    ? matches.map((m) => (
                        <TreeSeedingCard
                          key={m.id}
                          match={m}
                          teamById={teamById}
                          usedCodes={usedCodes}
                          allowedCodes={allowedCodes}
                          onSave={onSave}
                          height={SLOT - GAP}
                        />
                      ))
                    : Array.from({ length: col.count }).map((_, i) => (
                        <PlaceholderCard key={i} height={SLOT - GAP} />
                      ))}
                </TreeColumnInner>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TreeColumnInner({
  label,
  count,
  slot,
  gap,
  depth,
  children,
}: {
  label: string;
  count: number;
  slot: number;
  gap: number;
  depth: number;
  children: React.ReactNode;
}) {
  const slotHeight = slot * Math.pow(2, depth) - gap;
  const items = Array.isArray(children) ? children : [children];
  return (
    <>
      <div className="mb-2 flex items-center gap-2 px-1">
        <span className="font-display text-[10px] font-black uppercase tracking-[0.25em] text-primary">
          {label}
        </span>
        <div className="h-px w-8 bg-primary/40" />
      </div>
      <div className="flex flex-col" style={{ gap: `${gap}px` }}>
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="flex items-center justify-center transition-[height] duration-300 ease-out"
            style={{ height: slotHeight }}
          >
            {items[i]}
          </div>
        ))}
      </div>
    </>
  );
}

function PlaceholderCard({ height }: { height: number }) {
  return (
    <div
      className="w-52 rounded-xl border-2 border-dashed border-border/50 bg-muted/10 flex items-center justify-center"
      style={{ height }}
    >
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60">
        Auto-fills
      </span>
    </div>
  );
}

function TreeSeedingCard({
  match,
  teamById,
  usedCodes,
  allowedCodes,
  onSave,
  height,
}: {
  match: Match;
  teamById: Map<string, Team>;
  usedCodes: Set<string>;
  allowedCodes?: string[];
  onSave: SeedSaver;
  height: number;
}) {
  const homeTeam = match.home_team_id ? teamById.get(match.home_team_id) ?? null : null;
  const awayTeam = match.away_team_id ? teamById.get(match.away_team_id) ?? null : null;

  const [home, setHome] = useState<string | null>(homeTeam?.code.toUpperCase() ?? null);
  const [away, setAway] = useState<string | null>(awayTeam?.code.toUpperCase() ?? null);
  const [saving, setSaving] = useState(false);

  const initialHome = homeTeam?.code.toUpperCase() ?? null;
  const initialAway = awayTeam?.code.toUpperCase() ?? null;
  const dirty = home !== initialHome || away !== initialAway;
  const sameTeam = !!home && !!away && home === away;
  const lockedByResult = !!match.winner_team_id;

  // Exclude already-used codes from THIS match's pickers (don't exclude
  // this card's own current selections — they're allowed).
  const exclude = Array.from(usedCodes).filter(
    (c) => c !== initialHome && c !== initialAway,
  );

  const save = async () => {
    if (sameTeam) {
      toast.error("Home and away must be different teams");
      return;
    }
    setSaving(true);
    try {
      await onSave(match.id, home, away);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const isFilled = !!home && !!away;

  return (
    <article
      className={cn(
        "w-52 rounded-xl border-2 p-2 flex flex-col gap-1.5 transition-colors",
        lockedByResult
          ? "border-success/60 bg-success/5"
          : isFilled
            ? "border-primary/50 bg-card"
            : "border-border bg-card",
      )}
      style={{ height }}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] font-bold text-primary">
          {match.match_code}
        </span>
        {lockedByResult && (
          <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">
            <Lock className="w-2.5 h-2.5" />
            Locked
          </span>
        )}
      </div>
      <CountryCombobox
        value={home}
        onChange={setHome}
        placeholder="— Home —"
        disabled={lockedByResult}
        excludeCodes={[...exclude, ...(away ? [away] : [])]}
        allowCodes={allowedCodes}
      />
      <CountryCombobox
        value={away}
        onChange={setAway}
        placeholder="— Away —"
        disabled={lockedByResult}
        excludeCodes={[...exclude, ...(home ? [home] : [])]}
        allowCodes={allowedCodes}
      />
      {!lockedByResult && (
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving || sameTeam}
          variant={dirty ? "default" : "outline"}
          className="h-6 text-[11px] px-2"
        >
          <Save className="w-3 h-3" />
          Save
        </Button>
      )}
    </article>
  );
}

function SeedingRow({
  match,
  teamById,
  usedCodes,
  allowedCodes,
  onSave,
}: {
  match: Match;
  teamById: Map<string, Team>;
  usedCodes: Set<string>;
  allowedCodes?: string[];
  onSave: SeedSaver;
}) {
  const homeTeam = match.home_team_id ? teamById.get(match.home_team_id) ?? null : null;
  const awayTeam = match.away_team_id ? teamById.get(match.away_team_id) ?? null : null;
  const initialHome = homeTeam?.code.toUpperCase() ?? null;
  const initialAway = awayTeam?.code.toUpperCase() ?? null;

  const [home, setHome] = useState<string | null>(initialHome);
  const [away, setAway] = useState<string | null>(initialAway);
  const [saving, setSaving] = useState(false);

  const dirty = home !== initialHome || away !== initialAway;
  const sameTeam = !!home && !!away && home === away;
  const lockedByResult = !!match.winner_team_id;

  const exclude = Array.from(usedCodes).filter(
    (c) => c !== initialHome && c !== initialAway,
  );

  const save = async () => {
    if (sameTeam) {
      toast.error("Home and away must be different teams");
      return;
    }
    setSaving(true);
    try {
      await onSave(match.id, home, away);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="px-4 py-3 grid grid-cols-12 gap-3 items-center">
      <div className="col-span-2 sm:col-span-1">
        <div className="font-mono text-xs text-primary font-semibold">
          {match.match_code}
        </div>
      </div>
      <div className="col-span-10 sm:col-span-4">
        <CountryCombobox
          value={home}
          onChange={setHome}
          placeholder="— Home team —"
          disabled={lockedByResult}
          excludeCodes={[...exclude, ...(away ? [away] : [])]}
          allowCodes={allowedCodes}
        />
      </div>
      <div className="hidden sm:flex sm:col-span-1 justify-center text-xs text-muted-foreground">
        vs
      </div>
      <div className="col-span-12 sm:col-span-4">
        <CountryCombobox
          value={away}
          onChange={setAway}
          placeholder="— Away team —"
          disabled={lockedByResult}
          excludeCodes={[...exclude, ...(home ? [home] : [])]}
          allowCodes={allowedCodes}
        />
      </div>
      <div className="col-span-12 sm:col-span-2 flex justify-end">
        {lockedByResult ? (
          <span
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"
            title="Result entered — clear it first to change teams"
          >
            <Lock className="w-3 h-3" />
            Locked
          </span>
        ) : (
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving || sameTeam}
            variant={dirty ? "default" : "outline"}
          >
            <Save className="w-3.5 h-3.5" />
            Save
          </Button>
        )}
      </div>
    </li>
  );
}

/* ============================================================
   Results panel (unchanged from prior version)
============================================================ */

const ROUND_LABEL: Record<string, string> = {
  r32: "Round of 32",
  r16: "Round of 16",
  qf: "Quarter-finals",
  sf: "Semi-finals",
  third_place: "Third-place playoff",
  final: "Final",
};

type ResultPayload = {
  winnerTeamId: string | null;
  homeScore: number | null;
  awayScore: number | null;
  yellowCards: number | null;
  redCards: number | null;
  wentToExtraTime: boolean | null;
  wentToShootout: boolean | null;
  pksAwarded: number | null;
};

function ResultsPanel({
  matches,
  matchByCode: _matchByCode,
  teams,
  onSave,
}: {
  matches: Match[];
  matchByCode: Map<string, Match>;
  teams: Team[];
  onSave: (matchId: string, payload: ResultPayload) => Promise<void>;
}) {
  const teamById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);

  const grouped = useMemo(() => {
    const groups: Record<string, Match[]> = {};
    for (const m of matches) {
      groups[m.round] ??= [];
      groups[m.round].push(m);
    }
    return groups;
  }, [matches]);

  const order = ["r32", "r16", "qf", "sf", "third_place", "final"] as const;

  const totals = useMemo(() => {
    let yellow = 0;
    let red = 0;
    let goals = 0;
    let ot = 0;
    let so = 0;
    let pk = 0;
    let played = 0;
    for (const m of matches) {
      if (!m.winner_team_id) continue;
      played += 1;
      goals += m.total_goals ?? 0;
      yellow += m.yellow_cards ?? 0;
      red += m.red_cards ?? 0;
      if (m.went_to_extra_time) ot += 1;
      if (m.went_to_shootout) so += 1;
      pk += m.pks_awarded ?? 0;
    }
    return { yellow, red, goals, ot, so, pk, played };
  }, [matches]);

  const [view, setView] = useState<"list" | "tree">("tree");
  const [editingMatchId, setEditingMatchId] = useState<string | null>(null);
  const editingMatch = useMemo(
    () => matches.find((m) => m.id === editingMatchId) ?? null,
    [matches, editingMatchId],
  );

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-border bg-card p-3">
        <div className="mb-2">
          <h3 className="font-display text-sm font-bold text-foreground">
            Bonus Question Stats
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Live totals across all match results — used to score per-league bonus questions.
          </p>
          <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            Running totals · across {totals.played} completed match
            {totals.played === 1 ? "" : "es"}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-center">
          <Tot label="Goals" value={totals.goals} />
          <Tot label="Yellows" value={totals.yellow} />
          <Tot label="Reds" value={totals.red} />
          <Tot label="OT" value={totals.ot} />
          <Tot label="Shootouts" value={totals.so} />
          <Tot label="PKs awarded" value={totals.pk} />
        </div>
      </div>

      <div className="flex justify-end">
        <div className="inline-flex items-center rounded-md border border-border bg-background p-0.5">
          <button
            type="button"
            onClick={() => setView("list")}
            className={cn(
              "inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors",
              view === "list"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
            aria-pressed={view === "list"}
          >
            <List className="w-3 h-3" />
            List
          </button>
          <button
            type="button"
            onClick={() => setView("tree")}
            className={cn(
              "inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors",
              view === "tree"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
            aria-pressed={view === "tree"}
          >
            <GitBranch className="w-3 h-3" />
            Tree
          </button>
        </div>
      </div>

      {view === "list" ? (
        order.map((round) => {
          const group = grouped[round];
          if (!group?.length) return null;
          return (
            <div
              key={round}
              className="rounded-xl border border-border bg-card overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-border bg-muted/20">
                <div className="text-sm font-semibold text-foreground">
                  {ROUND_LABEL[round]}
                </div>
              </div>
              <ul className="divide-y divide-border">
                {group.map((m) => (
                  <ResultRow
                    key={m.id}
                    match={m}
                    teamById={teamById}
                    onSave={onSave}
                  />
                ))}
              </ul>
            </div>
          );
        })
      ) : (
        <ResultsTree
          grouped={grouped}
          teamById={teamById}
          onOpenEdit={(id) => setEditingMatchId(id)}
        />
      )}

      <p className="text-xs text-muted-foreground text-center">
        Saving a winner automatically populates the next round&apos;s matchup.
        Clearing a winner cascades clears downstream and wipes per-match stats.
      </p>

      <Sheet
        open={!!editingMatch}
        onOpenChange={(open) => {
          if (!open) setEditingMatchId(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-md overflow-y-auto"
        >
          {editingMatch && (
            <>
              <SheetHeader>
                <SheetTitle className="font-mono text-sm text-primary">
                  {editingMatch.match_code} ·{" "}
                  <span className="text-foreground font-display">
                    {ROUND_LABEL[editingMatch.round]}
                  </span>
                </SheetTitle>
                <SheetDescription>
                  Pick the winner and enter per-game stats. Saving advances the
                  bracket automatically.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4">
                <ResultEditor
                  key={editingMatch.id}
                  match={editingMatch}
                  teamById={teamById}
                  onSave={async (id, payload) => {
                    // Keep the sheet open while the save is in flight so the
                    // admin sees the "Saving…" indicator and the disabled
                    // button. Close only after the server write resolves.
                    await onSave(id, payload);
                    setEditingMatchId(null);
                  }}
                  onCancel={() => setEditingMatchId(null)}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ResultsTree({
  grouped,
  teamById,
  onOpenEdit,
}: {
  grouped: Record<string, Match[]>;
  teamById: Map<string, Team>;
  onOpenEdit: (matchId: string) => void;
}) {
  const SLOT = 108;
  const GAP = 12;
  const COLUMN_HEADER_H = 32;

  const COLUMNS: { label: string; round: keyof typeof ROUND_LABEL; count: number }[] = [
    { label: "R32", round: "r32", count: 16 },
    { label: "R16", round: "r16", count: 8 },
    { label: "QF", round: "qf", count: 4 },
    { label: "SF", round: "sf", count: 2 },
    { label: "Final", round: "final", count: 1 },
  ];

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const colRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [leftmostVisible, setLeftmostVisible] = useState(0);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;

    const recompute = () => {
      const rootLeft = root.getBoundingClientRect().left;
      let firstVisible = 0;
      for (let i = 0; i < colRefs.current.length; i++) {
        const el = colRefs.current[i];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.right > rootLeft + 16) {
          firstVisible = i;
          break;
        }
      }
      setLeftmostVisible((prev) => (prev === firstVisible ? prev : firstVisible));
    };

    recompute();
    root.addEventListener("scroll", recompute, { passive: true });
    window.addEventListener("resize", recompute);
    return () => {
      root.removeEventListener("scroll", recompute);
      window.removeEventListener("resize", recompute);
    };
  }, []);

  const visibleContentHeight = (() => {
    let max = 0;
    for (let i = leftmostVisible; i < COLUMNS.length; i++) {
      const depth = i - leftmostVisible;
      const slotH = SLOT * Math.pow(2, depth) - GAP;
      const count = COLUMNS[i].count;
      const colH = COLUMN_HEADER_H + count * slotH + Math.max(0, count - 1) * GAP;
      if (colH > max) max = colH;
    }
    return max;
  })();

  const thirdPlace = grouped["third_place"]?.[0] ?? null;

  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="mb-2 px-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        Tap any card to enter or edit a result.
      </p>
      <div
        ref={scrollRef}
        className="overflow-x-auto overflow-y-hidden pb-2 transition-[height] duration-300 ease-out"
        style={{ height: visibleContentHeight + 16 }}
      >
        <div className="flex items-stretch gap-6" style={{ minWidth: "max-content" }}>
          {COLUMNS.map((col, colIdx) => {
            const effectiveDepth = Math.max(0, colIdx - leftmostVisible);
            const roundMatches = grouped[col.round] ?? [];
            return (
              <div
                key={col.label}
                ref={(el) => {
                  colRefs.current[colIdx] = el;
                }}
                className="flex flex-col"
              >
                <TreeColumnInner
                  label={col.label}
                  count={col.count}
                  slot={SLOT}
                  gap={GAP}
                  depth={effectiveDepth}
                >
                  {Array.from({ length: col.count }).map((_, i) => {
                    const m = roundMatches[i];
                    return m ? (
                      <TreeResultCard
                        key={m.id}
                        match={m}
                        teamById={teamById}
                        onClick={() => onOpenEdit(m.id)}
                        height={SLOT * Math.pow(2, effectiveDepth) - GAP}
                      />
                    ) : (
                      <PlaceholderCard
                        key={i}
                        height={SLOT * Math.pow(2, effectiveDepth) - GAP}
                      />
                    );
                  })}
                </TreeColumnInner>
              </div>
            );
          })}
        </div>
      </div>

      {thirdPlace && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="mb-2 flex items-center gap-2 px-1">
            <span className="font-display text-[10px] font-black uppercase tracking-[0.25em] text-primary">
              Third-place playoff
            </span>
            <div className="h-px flex-1 bg-primary/20" />
          </div>
          <div className="max-w-[14rem]">
            <TreeResultCard
              match={thirdPlace}
              teamById={teamById}
              onClick={() => onOpenEdit(thirdPlace.id)}
              height={SLOT - GAP}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function TreeResultCard({
  match,
  teamById,
  onClick,
  height,
}: {
  match: Match;
  teamById: Map<string, Team>;
  onClick: () => void;
  height: number;
}) {
  const home = match.home_team_id ? teamById.get(match.home_team_id) ?? null : null;
  const away = match.away_team_id ? teamById.get(match.away_team_id) ?? null : null;
  const winnerId = match.winner_team_id;
  const hasResult = !!winnerId;
  const ready = !!home && !!away;

  const teamRow = (team: Team | null, isWinner: boolean) => (
    <div
      className={cn(
        "flex items-center gap-1.5 px-1.5 py-1 rounded text-xs min-w-0",
        hasResult && isWinner && "bg-success/15 text-success font-semibold",
        hasResult && !isWinner && "text-muted-foreground line-through opacity-70",
      )}
    >
      {team && <Flag code={team.code} size={12} />}
      <span className={cn("truncate", !team && "italic text-muted-foreground")}>
        {team?.name ?? "TBD"}
      </span>
      {team && <RankBadge code={team.code} size="xs" />}
    </div>
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-52 rounded-xl border-2 p-1.5 flex flex-col gap-1 transition-colors text-left",
        "hover:border-primary/60 hover:bg-muted/30 focus:outline-none focus:ring-2 focus:ring-primary/40",
        hasResult
          ? "border-success/50 bg-success/5"
          : ready
            ? "border-primary/40 bg-card"
            : "border-border bg-card",
      )}
      style={{ height }}
    >
      <div className="flex items-center justify-between px-1">
        <span className="font-mono text-[10px] font-bold text-primary">
          {match.match_code}
        </span>
        {hasResult ? (
          <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-success/15 text-success border border-success/30">
            Done
          </span>
        ) : ready ? (
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
            Tap to enter
          </span>
        ) : (
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
            Awaiting teams
          </span>
        )}
      </div>
      <div className="flex flex-col gap-0.5 min-h-0 flex-1 justify-center">
        {teamRow(home, !!winnerId && winnerId === home?.id)}
        {teamRow(away, !!winnerId && winnerId === away?.id)}
      </div>
      {hasResult && match.total_goals != null && (
        <div className="px-1 text-[10px] text-muted-foreground font-mono flex items-center gap-2">
          <span>{match.total_goals}g</span>
          {(match.yellow_cards ?? 0) > 0 && <span>{match.yellow_cards}y</span>}
          {(match.red_cards ?? 0) > 0 && <span>{match.red_cards}r</span>}
          {match.went_to_extra_time && <span>OT</span>}
          {match.went_to_shootout && <span>SO</span>}
        </div>
      )}
    </button>
  );
}

function Tot({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-muted/30 px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">
        {label}
      </div>
      <div className="font-display text-base font-bold text-foreground tabular-nums">
        {value}
      </div>
    </div>
  );
}

function ResultEditor({
  match,
  teamById,
  onSave,
  onCancel,
}: {
  match: Match;
  teamById: Map<string, Team>;
  onSave: (matchId: string, payload: ResultPayload) => Promise<void>;
  onCancel: () => void;
}) {
  const home = match.home_team_id ? teamById.get(match.home_team_id) ?? null : null;
  const away = match.away_team_id ? teamById.get(match.away_team_id) ?? null : null;

  const [winner, setWinner] = useState<string | null>(match.winner_team_id);
  const [homeScore, setHomeScore] = useState<string>(
    match.home_score != null ? String(match.home_score) : "",
  );
  const [awayScore, setAwayScore] = useState<string>(
    match.away_score != null ? String(match.away_score) : "",
  );
  const [yellow, setYellow] = useState<string>(
    match.yellow_cards != null ? String(match.yellow_cards) : "",
  );
  const [red, setRed] = useState<string>(
    match.red_cards != null ? String(match.red_cards) : "",
  );
  const [ot, setOt] = useState<boolean>(!!match.went_to_extra_time);
  const [so, setSo] = useState<boolean>(!!match.went_to_shootout);
  const [pk, setPk] = useState<string>(
    match.pks_awarded != null ? String(match.pks_awarded) : "",
  );
  const [saving, setSaving] = useState(false);
  const [staged, setStaged] = useState<ResultPayload | null>(null);
  const [recapOpen, setRecapOpen] = useState(false);

  const intToStr = (n: number | null | undefined) => (n == null ? "" : String(n));
  const scoreOrWinnerChanged =
    winner !== match.winner_team_id ||
    homeScore !== intToStr(match.home_score) ||
    awayScore !== intToStr(match.away_score) ||
    ot !== !!match.went_to_extra_time ||
    so !== !!match.went_to_shootout;

  const ready = !!home && !!away;

  const parseInt0 = (s: string): number | null => {
    if (s === "") return null;
    const n = Number(s);
    return Number.isFinite(n) && Number.isInteger(n) ? n : Number.NaN;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (!winner) {
        await onSave(match.id, {
          winnerTeamId: null,
          homeScore: null,
          awayScore: null,
          yellowCards: null,
          redCards: null,
          wentToExtraTime: null,
          wentToShootout: null,
          pksAwarded: null,
        });
        return;
      }

      const homeNum = parseInt0(homeScore);
      const awayNum = parseInt0(awayScore);
      const yellowNum = parseInt0(yellow);
      const redNum = parseInt0(red);
      const pkNum = parseInt0(pk);

      const numbers: Array<[string, number | null]> = [
        [`${home?.name ?? "Home"} score`, homeNum],
        [`${away?.name ?? "Away"} score`, awayNum],
        ["Yellow cards", yellowNum],
        ["Red cards", redNum],
        ["PKs awarded", pkNum],
      ];
      for (const [label, n] of numbers) {
        if (n === null || Number.isNaN(n) || n < 0) {
          toast.error(`${label} must be a non-negative whole number`);
          return;
        }
      }

      if (so && homeNum !== awayNum) {
        toast.error(
          "Shootout matches must have equal scores after extra time",
        );
        return;
      }
      if (!so && homeNum === awayNum) {
        toast.error("Non-shootout matches cannot end in a tied score");
        return;
      }
      if (!so) {
        const higherIsHome = (homeNum ?? 0) > (awayNum ?? 0);
        const expectedWinner = higherIsHome ? home?.id : away?.id;
        if (expectedWinner && expectedWinner !== winner) {
          toast.error("Winner must be the team with the higher score");
          return;
        }
      }

      const payload: ResultPayload = {
        winnerTeamId: winner,
        homeScore: homeNum,
        awayScore: awayNum,
        yellowCards: yellowNum,
        redCards: redNum,
        wentToExtraTime: ot,
        wentToShootout: so,
        pksAwarded: pkNum,
      };

      // Gate: if winner/score/AET/shootout changed, force the combined
      // Goblin recap flow. Stats-only edits save directly.
      if (scoreOrWinnerChanged) {
        setStaged(payload);
        setRecapOpen(true);
        return;
      }

      await onSave(match.id, payload);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!ready) {
    return (
      <div className="rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
        Both teams must be set before a result can be recorded. The bracket
        will fill this match in once the upstream round is decided.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
          Matchup
        </div>
        <div className="flex items-center gap-2">
          <TeamLabel team={home} />
          <span className="text-muted-foreground text-xs">vs</span>
          <TeamLabel team={away} />
        </div>
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          Winner *
        </Label>
        <select
          value={winner ?? ""}
          onChange={(e) => setWinner(e.target.value || null)}
          className={cn(
            "mt-1 w-full rounded-md border border-border bg-input text-foreground px-2 py-2 text-sm",
            "focus:outline-none focus:ring-2 focus:ring-primary/30",
          )}
        >
          <option value="">— Pick winner —</option>
          {home && <option value={home.id}>{home.name} wins</option>}
          {away && <option value={away.id}>{away.name} wins</option>}
        </select>
        {winner && (
          <button
            type="button"
            onClick={() => setWinner(null)}
            className="mt-1 text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          >
            Clear result
          </button>
        )}
      </div>

      {match.winner_team_id && home && away && (
        <div className="rounded-lg border border-border bg-card p-3 flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            Goblin recap (Stage 1): paste a Gemini response so the feed gets a
            real match recap above the Goblin's riff.
          </div>
          <MatchRecapButton
            matchKind="knockout"
            matchId={match.id}
            prompt={{
              homeTeam: home.name,
              awayTeam: away.name,
              homeScore: match.home_score ?? 0,
              awayScore: match.away_score ?? 0,
              aet: !!match.went_to_extra_time,
              pens: !!match.went_to_shootout,
              stageLabel: ROUND_LABEL[match.round as keyof typeof ROUND_LABEL] ?? match.round,
              venueCity: null,
              venueCountry: null,
              matchDateISO: match.scheduled_at ?? null,
            }}
          />
        </div>
      )}

      {winner && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <StatField
              label={`${home?.name ?? "Home"} score`}
              value={homeScore}
              onChange={setHomeScore}
              required
            />
            <StatField
              label={`${away?.name ?? "Away"} score`}
              value={awayScore}
              onChange={setAwayScore}
              required
            />
          </div>
          <p className="text-[10px] text-muted-foreground -mt-2">
            Total goals (auto):{" "}
            <span className="font-mono text-foreground">
              {(() => {
                const h = homeScore === "" ? null : Number(homeScore);
                const a = awayScore === "" ? null : Number(awayScore);
                if (h === null || a === null || Number.isNaN(h) || Number.isNaN(a)) return "—";
                return h + a;
              })()}
            </span>
            . For shootouts, enter the score after extra time (PK shootout goals don&apos;t count).
          </p>
          <div className="grid grid-cols-2 gap-3">
            <StatField label="Yellow cards" value={yellow} onChange={setYellow} required />
            <StatField label="Red cards" value={red} onChange={setRed} required />
            <StatField label="PKs awarded" value={pk} onChange={setPk} required />
            <BoolField
              label="Extra time?"
              value={ot}
              onChange={(v) => {
                setOt(v);
                if (!v) setSo(false);
              }}
            />
            <BoolField
              label="Shootout?"
              value={so}
              onChange={setSo}
              disabled={!ot}
              hint={!ot ? "Requires extra time first" : undefined}
            />
          </div>
        </>
      )}

      <div className="flex justify-end items-center gap-2 pt-2 border-t border-border">
        {saving && (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground mr-auto">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Saving… this can take a few seconds
          </span>
        )}
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Save className="w-3.5 h-3.5" />
          )}
          {saving
            ? "Saving…"
            : !winner
              ? "Clear result"
              : scoreOrWinnerChanged
                ? "Save & add recap…"
                : "Save result"}
        </Button>
      </div>
      {staged && home && away && (
        <GoblinRecapDialog
          open={recapOpen}
          onOpenChange={(o) => {
            setRecapOpen(o);
            if (!o) setStaged(null);
          }}
          matchKind="knockout"
          matchId={match.id}
          prompt={{
            homeTeam: home.name,
            awayTeam: away.name,
            homeScore: staged.homeScore ?? 0,
            awayScore: staged.awayScore ?? 0,
            aet: !!staged.wentToExtraTime,
            pens: !!staged.wentToShootout,
            stageLabel:
              ROUND_LABEL[match.round as keyof typeof ROUND_LABEL] ?? match.round,
            venueCity: null,
            venueCountry: null,
            matchDateISO: match.scheduled_at ?? null,
          }}
          preSave={async () => {
            await onSave(match.id, staged);
          }}
          onSaved={() => {
            setStaged(null);
            toast.success("Result + recap saved");
          }}
        />
      )}
    </div>
  );
}

function ResultRow({
  match,
  teamById,
  onSave,
}: {
  match: Match;
  teamById: Map<string, Team>;
  onSave: (matchId: string, payload: ResultPayload) => Promise<void>;
}) {
  const home = match.home_team_id ? teamById.get(match.home_team_id) : null;
  const away = match.away_team_id ? teamById.get(match.away_team_id) : null;

  const [winner, setWinner] = useState<string | null>(match.winner_team_id);
  const [homeScore, setHomeScore] = useState<string>(
    match.home_score != null ? String(match.home_score) : "",
  );
  const [awayScore, setAwayScore] = useState<string>(
    match.away_score != null ? String(match.away_score) : "",
  );
  const [yellow, setYellow] = useState<string>(
    match.yellow_cards != null ? String(match.yellow_cards) : "",
  );
  const [red, setRed] = useState<string>(
    match.red_cards != null ? String(match.red_cards) : "",
  );
  const [ot, setOt] = useState<boolean>(!!match.went_to_extra_time);
  const [so, setSo] = useState<boolean>(!!match.went_to_shootout);
  const [pk, setPk] = useState<string>(
    match.pks_awarded != null ? String(match.pks_awarded) : "",
  );
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<boolean>(!!match.winner_team_id);
  const [staged, setStaged] = useState<ResultPayload | null>(null);
  const [recapOpen, setRecapOpen] = useState(false);

  const ready = !!home && !!away;
  const intToStr = (n: number | null) => (n == null ? "" : String(n));
  const scoreOrWinnerChanged =
    winner !== match.winner_team_id ||
    homeScore !== intToStr(match.home_score) ||
    awayScore !== intToStr(match.away_score) ||
    ot !== !!match.went_to_extra_time ||
    so !== !!match.went_to_shootout;
  const dirty =
    scoreOrWinnerChanged ||
    yellow !== intToStr(match.yellow_cards) ||
    red !== intToStr(match.red_cards) ||
    pk !== intToStr(match.pks_awarded);

  const parseInt0 = (s: string): number | null => {
    if (s === "") return null;
    const n = Number(s);
    return Number.isFinite(n) && Number.isInteger(n) ? n : Number.NaN;
  };

  const save = async () => {
    setSaving(true);
    try {
      if (!winner) {
        await onSave(match.id, {
          winnerTeamId: null,
          homeScore: null,
          awayScore: null,
          yellowCards: null,
          redCards: null,
          wentToExtraTime: null,
          wentToShootout: null,
          pksAwarded: null,
        });
        return;
      }

      const homeNum = parseInt0(homeScore);
      const awayNum = parseInt0(awayScore);
      const yellowNum = parseInt0(yellow);
      const redNum = parseInt0(red);
      const pkNum = parseInt0(pk);

      const numbers: Array<[string, number | null]> = [
        [`${home?.name ?? "Home"} score`, homeNum],
        [`${away?.name ?? "Away"} score`, awayNum],
        ["Yellow cards", yellowNum],
        ["Red cards", redNum],
        ["PKs awarded", pkNum],
      ];
      for (const [label, n] of numbers) {
        if (n === null || Number.isNaN(n) || n < 0) {
          toast.error(`${label} must be a non-negative whole number`);
          return;
        }
      }

      if (so && homeNum !== awayNum) {
        toast.error(
          "Shootout matches must have equal scores after extra time",
        );
        return;
      }
      if (!so && homeNum === awayNum) {
        toast.error("Non-shootout matches cannot end in a tied score");
        return;
      }
      if (!so) {
        const higherIsHome = (homeNum ?? 0) > (awayNum ?? 0);
        const expectedWinner = higherIsHome ? home?.id : away?.id;
        if (expectedWinner && expectedWinner !== winner) {
          toast.error("Winner must be the team with the higher score");
          return;
        }
      }

      const payload: ResultPayload = {
        winnerTeamId: winner,
        homeScore: homeNum,
        awayScore: awayNum,
        yellowCards: yellowNum,
        redCards: redNum,
        wentToExtraTime: ot,
        wentToShootout: so,
        pksAwarded: pkNum,
      };

      // Gate: winner/score/AET/shootout changes require a Goblin recap.
      // Stats-only edits (cards, PKs) save directly.
      if (scoreOrWinnerChanged) {
        setStaged(payload);
        setRecapOpen(true);
        return;
      }

      await onSave(match.id, payload);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const hasResult = !!match.winner_team_id;

  return (
    <li className="px-4 py-3">
      <div className="grid grid-cols-12 gap-3 items-center">
        <div className="col-span-2 sm:col-span-1 font-mono text-xs text-primary font-semibold">
          {match.match_code}
        </div>
        <div className="col-span-10 sm:col-span-5 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <TeamLabel team={home} />
            <span className="text-muted-foreground text-xs">vs</span>
            <TeamLabel team={away} />
            {hasResult && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-success/15 text-success border border-success/30">
                Result in
              </span>
            )}
          </div>
        </div>
        <div className="col-span-12 sm:col-span-4">
          <select
            value={winner ?? ""}
            disabled={!ready}
            onChange={(e) => {
              const v = e.target.value || null;
              setWinner(v);
              if (v) setExpanded(true);
            }}
            className={cn(
              "w-full rounded-md border border-border bg-input text-foreground px-2 py-1.5 text-sm",
              "focus:outline-none focus:ring-2 focus:ring-primary/30",
              !ready && "opacity-50 cursor-not-allowed",
            )}
          >
            <option value="">{ready ? "— Pick winner —" : "Waiting on teams"}</option>
            {home && <option value={home.id}>{home.name} wins</option>}
            {away && <option value={away.id}>{away.name} wins</option>}
          </select>
        </div>
        <div className="col-span-12 sm:col-span-2 flex justify-end gap-2">
          {winner && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
            >
              {expanded ? "Hide stats" : "Show stats"}
            </button>
          )}
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving || !ready}
            variant={dirty ? "default" : "outline"}
            title={
              scoreOrWinnerChanged && winner
                ? "Saving a knockout result requires adding a Goblin recap"
                : undefined
            }
          >
            <Save className="w-3.5 h-3.5" />
            {scoreOrWinnerChanged && winner ? "Save & recap…" : "Save"}
          </Button>
        </div>
      </div>

      {winner && expanded && (
        <div className="mt-3 rounded-lg border border-border bg-muted/20 p-3 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatField
              label={`${home?.name ?? "Home"} score`}
              value={homeScore}
              onChange={setHomeScore}
              required
            />
            <StatField
              label={`${away?.name ?? "Away"} score`}
              value={awayScore}
              onChange={setAwayScore}
              required
            />
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Total goals (auto)
              </span>
              <div className="h-9 px-2 flex items-center font-mono text-sm rounded-md border border-border bg-muted/40 text-foreground">
                {(() => {
                  const h = homeScore === "" ? null : Number(homeScore);
                  const a = awayScore === "" ? null : Number(awayScore);
                  if (h === null || a === null || Number.isNaN(h) || Number.isNaN(a)) return "—";
                  return h + a;
                })()}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatField label="Yellow cards" value={yellow} onChange={setYellow} required />
            <StatField label="Red cards" value={red} onChange={setRed} required />
            <StatField label="PKs awarded" value={pk} onChange={setPk} required />
            <BoolField
              label="Extra time?"
              value={ot}
              onChange={(v) => {
                setOt(v);
                if (!v) setSo(false);
              }}
            />
            <BoolField
              label="Shootout?"
              value={so}
              onChange={setSo}
              disabled={!ot}
              hint={!ot ? "Requires extra time first" : undefined}
            />
          </div>
        </div>
      )}
      {staged && home && away && (
        <GoblinRecapDialog
          open={recapOpen}
          onOpenChange={(o) => {
            setRecapOpen(o);
            if (!o) setStaged(null);
          }}
          matchKind="knockout"
          matchId={match.id}
          prompt={{
            homeTeam: home.name,
            awayTeam: away.name,
            homeScore: staged.homeScore ?? 0,
            awayScore: staged.awayScore ?? 0,
            aet: !!staged.wentToExtraTime,
            pens: !!staged.wentToShootout,
            stageLabel:
              ROUND_LABEL[match.round as keyof typeof ROUND_LABEL] ?? match.round,
            venueCity: null,
            venueCountry: null,
            matchDateISO: match.scheduled_at ?? null,
          }}
          preSave={async () => {
            await onSave(match.id, staged);
          }}
          onSaved={() => {
            setStaged(null);
            toast.success("Result + recap saved");
          }}
        />
      )}
    </li>
  );
}

function StatField({
  label,
  value,
  onChange,
  required,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
        {label}
        {required && <span className="text-primary"> *</span>}
      </span>
      <Input
        type="number"
        inputMode="numeric"
        min={0}
        max={50}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ""))}
        className={cn("text-sm", disabled && "opacity-50")}
      />
    </label>
  );
}

function BoolField({
  label,
  value,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <label className={cn("flex flex-col gap-1", disabled && "opacity-60")}>
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
        {label} <span className="text-primary">*</span>
      </span>
      <div
        className={cn(
          "flex items-center gap-2 h-9 px-2 rounded-md border border-border bg-input",
          disabled && "cursor-not-allowed",
        )}
      >
        <Switch checked={value} onCheckedChange={onChange} disabled={disabled} />
        <span className="text-xs text-muted-foreground">{value ? "Yes" : "No"}</span>
      </div>
      {hint && (
        <span className="text-[10px] text-muted-foreground italic">{hint}</span>
      )}
    </label>
  );
}
